import { createContext, useContext, useMemo, useState } from 'react';
import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';
import { enableNewReviewSaveModal } from '@gonfalon/dogfood-flags/dogfood-flags';
import { noop } from 'lodash';

export interface ReviewAndSaveContextValue {
  isCustomScheduleSelected: boolean;
  setIsCustomScheduleSelected: (value: boolean) => void;
  isJsonDiffingEnabled: boolean;
  setIsJsonDiffingEnabled: (value: boolean) => void;
  isInitializing: boolean;
  setIsInitializing: (value: boolean) => void;
  expandAll: boolean | undefined;
  setExpandAll: (value: boolean | undefined) => void;
  testRunEnabled: boolean;
  setTestRunEnabled: (value: boolean) => void;
  collapseState: Record<string, boolean | undefined>;
  setCollapseState: (id: string, open: boolean) => void;
}

export const ReviewAndSaveContext = createContext<ReviewAndSaveContextValue | undefined>(undefined);

const fallthroughSetIsCustomScheduleSelected = (value: boolean) => {
  if (value && enableNewReviewSaveModal()) {
    throw new Error('useReviewAndSaveContext must be used within a CustomScheduleContextProvider');
  }
};

export const ReviewAndSaveContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [isInitializing, setIsInitializing] = useState(enableNewReviewSaveModal());
  const [testRunEnabled, setTestRunEnabled] = useState(false);
  const [isCustomScheduleSelected, setIsCustomScheduleSelected] = useState(false);
  const [isJsonDiffingEnabled, setIsJsonDiffingEnabled] = useState(false);
  const [savedExpandAll, slot] = useStorageSlot('expand-all-instructions', {
    storageType: 'localStorage',
  });
  const [collapseState, setCollapseState] = useState<Record<string, boolean | undefined>>({});

  const value = useMemo(
    () => ({
      isInitializing,
      setIsInitializing,
      isCustomScheduleSelected,
      setIsCustomScheduleSelected,
      isJsonDiffingEnabled,
      setIsJsonDiffingEnabled,
      expandAll: savedExpandAll !== 'false',
      setExpandAll(slotValue: boolean | undefined) {
        slot.set(slotValue ? 'true' : 'false');

        setCollapseState((prev) => {
          const nextState = { ...prev };
          Object.keys(prev).forEach((key) => {
            nextState[key] = slotValue;
          });
          return nextState;
        });
      },
      testRunEnabled,
      setTestRunEnabled,
      collapseState,
      setCollapseState(id: string, open: boolean) {
        setCollapseState((prev) => ({
          ...prev,
          [id]: open,
        }));
      },
    }),
    [
      isCustomScheduleSelected,
      isInitializing,
      collapseState,
      savedExpandAll,
      slot,
      isJsonDiffingEnabled,
      testRunEnabled,
    ],
  );

  return <ReviewAndSaveContext.Provider value={value}>{children}</ReviewAndSaveContext.Provider>;
};

export const useReviewAndSaveContext = (): ReviewAndSaveContextValue => {
  const context = useContext(ReviewAndSaveContext);

  // if the context is not found, assume that scheduling is not enabled
  // eg variation changes, flag copy

  return (
    context ?? {
      isCustomScheduleSelected: false,
      setIsCustomScheduleSelected: fallthroughSetIsCustomScheduleSelected,
      isJsonDiffingEnabled: false,
      setIsJsonDiffingEnabled: noop,
      isInitializing: false,
      setIsInitializing: noop,
      expandAll: undefined,
      setExpandAll: noop,
      testRunEnabled: false,
      setTestRunEnabled: noop,
      collapseState: {},
      setCollapseState: noop,
    }
  );
};
